import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Link, CircularProgress, Alert, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Collapse
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { KeyboardArrowDown as ExpandMoreIcon, KeyboardArrowUp as ExpandLessIcon } from '@mui/icons-material';
import { getTsiTicketsByAssigneeAndMonth, getTsiTeamConfig } from '../../services/apiService';

// Implicit catch-all group for assignees not placed in any configured group.
const OTHERS_CATEGORY = 'Others';

// Helper function to extract name from email format
const extractNameFromEmail = (fullEmail) => {
  if (!fullEmail) return 'Unknown';
  const parts = fullEmail.split('<');
  if (parts.length > 0) return parts[0].trim();
  return fullEmail;
};

// Helper function to format ADO URL
const formatAdoUrl = (id) => {
  if (!id) return '#';
  // Remove any non-numeric characters from the ID
  const numericId = id.toString().replace(/\D/g, '');
  return `https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${numericId}`;
};


const TsiAssigneePivotTable = ({ data }) => {
  const [pivotData, setPivotData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedCell, setSelectedCell] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [ticketsData, setTicketsData] = useState([]);
  const [selectedAssignee, setSelectedAssignee] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');

  // Admin-configured assignee groups (from /api/tsi-tickets/team-config). Ordered list of
  // group names drives the pivot rows; "Others" is always appended for unassigned people.
  const [teamGroups, setTeamGroups] = useState([]); // [{ id, name, members: [] }]

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await getTsiTeamConfig();
        if (!cancelled && resp.success && resp.data) setTeamGroups(resp.data.groups || []);
      } catch (err) {
        console.error('Error loading TSI team config:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Ordered category names shown in the table: configured groups first, then Others.
  const categoryNames = useMemo(
    () => [...teamGroups.map((g) => g.name), OTHERS_CATEGORY],
    [teamGroups]
  );

  // Map an assignee → category name using the configured membership; unlisted → Others.
  const memberToCategory = useMemo(() => {
    const map = {};
    teamGroups.forEach((g) => (g.members || []).forEach((m) => { map[m] = g.name; }));
    return map;
  }, [teamGroups]);
  const categorizeAssignee = (assignee) => memberToCategory[assignee] || OTHERS_CATEGORY;

  // For debugging
  useEffect(() => {
    console.log("TsiAssigneePivotTable data:", data);
  }, [data]);
  
  // Extract unique months from the data
  const months = useMemo(() => {
    // Check if we're receiving data from the new API endpoint
    if (data && data.data && data.data.months) {
      // New API format
      return data.data.months.map(month => `${month.monthName} ${month.year}`);
    } else if (data && Array.isArray(data)) {
      // Old API format
      return [...new Set(data.map(item => item.month))];
    }
    return [];
  }, [data]);

  // Process data into a pivot format
  useEffect(() => {
    if (!data || data.length === 0) return;
    
    // Check if we're receiving data from the new API endpoint
    if (data.data && data.data.assignees) {
      console.log("Received data from new API endpoint:", data);
      
      // Create pivot data from the new API response format
      const pivotObj = {};
      
      data.data.assignees.forEach(assignee => {
        pivotObj[assignee] = {};
        data.data.months.forEach(monthData => {
          const monthKey = `${monthData.monthName} ${monthData.year}`;
          pivotObj[assignee][monthKey] = 0;
        });
      });
      
      // Fill in the data
      data.data.data.forEach(assigneeData => {
        const assignee = assigneeData.assignee;
        assigneeData.months.forEach(monthData => {
          const monthKey = `${data.data.months.find(m => m.month === monthData.month && m.year === monthData.year).monthName} ${monthData.year}`;
          pivotObj[assignee][monthKey] = monthData.count;
        });
      });
      
      console.log("Generated pivot data from new API:", pivotObj);
      setPivotData(pivotObj);
      return;
    }
    
    // Legacy code for handling the old data format
    // First, identify all unique assignees in the data
    const assigneeSet = new Set();
    
    data.forEach(item => {
      // Look for assignee data in the structure
      Object.keys(item).forEach(key => {
        // Check if this is an assignee key (not month or total)
        if (key !== 'month' && key !== 'total') {
          assigneeSet.add(key);
        }
      });
    });
    
    const allAssignees = Array.from(assigneeSet);
    console.log("All assignees found:", allAssignees);
    
    // Create pivot data
    const pivotObj = {};
    
    allAssignees.forEach(assignee => {
      pivotObj[assignee] = {};
      months.forEach(month => {
        pivotObj[assignee][month] = 0;
      });
    });
    
    // Fill in the data
    data.forEach(item => {
      const month = item.month;
      allAssignees.forEach(assignee => {
        if (item[assignee] !== undefined) {
          pivotObj[assignee][month] = item[assignee];
        }
      });
    });
    
    console.log("Generated pivot data from legacy format:", pivotObj);
    setPivotData(pivotObj);
  }, [data, months]);

  // Calculate totals and group data by category. Categories are dynamic (from the admin
  // TSI Teams config) with "Others" always present for unassigned people.
  const calculateTotalsAndGroupData = () => {
    // Initialize grouped data + category totals for every configured category.
    const groupedData = {};
    const categoryTotals = {};
    categoryNames.forEach((name) => { groupedData[name] = []; categoryTotals[name] = 0; });

    if (!pivotData || Object.keys(pivotData).length === 0) {
      return { rowTotals: {}, columnTotals: {}, categoryTotals, groupedData };
    }

    const rowTotals = {};
    const columnTotals = {};

    months.forEach(month => {
      columnTotals[month] = 0;
    });
    
    // Calculate row totals and group assignees
    Object.keys(pivotData).forEach(assignee => {
      rowTotals[assignee] = 0;
      
      months.forEach(month => {
        const value = pivotData[assignee]?.[month] || 0;
        rowTotals[assignee] += value;
        columnTotals[month] += value;
      });
      
      // Categorize the assignee
      const category = categorizeAssignee(assignee);
      
      // Add to the appropriate group
      groupedData[category].push(assignee);
      
      // Add to category totals
      categoryTotals[category] = (categoryTotals[category] || 0) + rowTotals[assignee];
    });
    
    // Sort assignees within each group by their totals
    Object.keys(groupedData).forEach(category => {
      groupedData[category].sort((a, b) => (rowTotals[b] || 0) - (rowTotals[a] || 0));
    });
    
    return { rowTotals, columnTotals, categoryTotals, groupedData };
  };

  const { rowTotals, columnTotals, categoryTotals, groupedData } = calculateTotalsAndGroupData();
  
  // State for expanded categories - all collapsed by default. Keyed by category name;
  // an absent key reads as collapsed, so this works for any dynamic set of groups.
  const [expandedCategories, setExpandedCategories] = useState({});
  
  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Handle cell click to view tickets
  const handleCellClick = async (assignee, month) => {
    try {
      setLoading(true);
      setError(null);
      setSelectedCell({ assignee, month });
      setSelectedAssignee(assignee);
      setSelectedMonth(month);
      
      const [monthName, year] = month.split(' ');
      const monthIndex = new Date(`${monthName} 1, ${year}`).getMonth() + 1;
      const formattedMonth = `${year}-${monthIndex.toString().padStart(2, '0')}`;
      
      // We need to pass the assignee to the API
      const response = await getTsiTicketsByAssigneeAndMonth(assignee, formattedMonth);
      
      if (response.success) {
        // Log the ticket data to debug
        console.log("Ticket data received:", response.data);
        if (response.data && response.data.length > 0) {
          console.log("First ticket:", response.data[0]);
        }
        
        setTicketsData(response.data);
        setDialogOpen(true);
      } else {
        throw new Error(response.message || 'Failed to fetch tickets');
      }
    } catch (err) {
      console.error('Error fetching tickets:', err);
      setError(err.message || 'Failed to fetch tickets');
    } finally {
      setLoading(false);
      setSelectedCell(null);
    }
  };
  
  // Handle dialog close
  const handleDialogClose = () => {
    setDialogOpen(false);
    setTicketsData([]);
  };

  // Get color based on value
  const getCellColor = (value) => {
    if (!value || value === 0) return '#FFFFFF';
    if (value < 5) return '#E3F2FD';
    if (value < 10) return '#BBDEFB';
    if (value < 20) return '#90CAF9';
    if (value < 30) return '#64B5F6';
    if (value < 50) return '#42A5F5';
    if (value < 75) return '#2196F3';
    if (value < 100) return '#1E88E5';
    return '#1565C0';
  };

  // Get text color based on background color
  const getTextColor = (bgColor) => {
    if (bgColor === '#2196F3' || bgColor === '#1E88E5' || bgColor === '#1565C0') return '#FFFFFF';
    return '#000000';
  };

  if (!data || data.length === 0) {
    return (
      <Box mt={2}>
        <Typography variant="subtitle1" color="textSecondary">
          No data available for pivot table
        </Typography>
      </Box>
    );
  }

  return (
    <Box mt={2}>
      <Box sx={{ backgroundColor: '#1976d2', color: 'white', p: 1.5, borderTopLeftRadius: 4, borderTopRightRadius: 4, mb: 0 }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
          Assignee Pivot Table (Click on a cell to view tickets)
        </Typography>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <TableContainer component={Paper} sx={{ maxHeight: 600, width: '100%', overflow: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>Assignee</TableCell>
              {months.map(month => (
                <TableCell key={month} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#f5f5f5' }}>
                  {month}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }}>Total</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* One collapsible block per category (configured groups + Others) */}
            {categoryNames.map((category) => (
              <React.Fragment key={category}>
                <TableRow
                  sx={{
                    backgroundColor: '#e3f2fd',
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: '#bbdefb' }
                  }}
                  onClick={() => toggleCategory(category)}
                >
                  <TableCell component="th" scope="row" sx={{ fontWeight: 'bold' }}>
                    <Box display="flex" alignItems="center">
                      {expandedCategories[category] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      {category}
                      <Box component="span" sx={{ ml: 1, color: 'text.secondary', fontWeight: 400 }}>
                        ({groupedData[category]?.length || 0})
                      </Box>
                    </Box>
                  </TableCell>
                  {months.map(month => (
                    <TableCell key={`${category}-${month}`} align="center" sx={{ fontWeight: 'bold' }}>
                      {(groupedData[category] || []).reduce((sum, assignee) =>
                        sum + (pivotData[assignee]?.[month] || 0), 0
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#bbdefb' }}>
                    {categoryTotals[category] || 0}
                  </TableCell>
                </TableRow>

                {/* Category members (collapsible) */}
                <TableRow>
                  <TableCell style={{ padding: 0 }} colSpan={months.length + 2}>
                    <Collapse in={!!expandedCategories[category]} timeout="auto" unmountOnExit>
                      <Table size="small">
                        <TableBody>
                          {(groupedData[category] || []).map(assignee => (
                            <TableRow key={assignee} hover>
                              <TableCell component="th" scope="row" sx={{ pl: 4 }}>
                                {extractNameFromEmail(assignee)}
                              </TableCell>
                              {months.map(month => {
                                const value = pivotData[assignee]?.[month] || 0;
                                const bgColor = getCellColor(value);
                                const textColor = getTextColor(bgColor);
                                return (
                                  <TableCell
                                    key={`${assignee}-${month}`}
                                    align="center"
                                    sx={{
                                      backgroundColor: bgColor,
                                      color: textColor,
                                      cursor: value > 0 ? 'pointer' : 'default',
                                      '&:hover': value > 0 ? { backgroundColor: '#64B5F6', color: '#000000' } : {}
                                    }}
                                    onClick={() => value > 0 && handleCellClick(assignee, month)}
                                  >
                                    {loading && selectedCell?.assignee === assignee && selectedCell?.month === month ? (
                                      <CircularProgress size={20} />
                                    ) : (
                                      <Tooltip title={value > 0 ? `Click to view ${value} tickets` : 'No tickets'} arrow>
                                        <Box component="span">
                                          {value > 0 ? (
                                            <Link component="button" underline="none" color="inherit" onClick={(e) => e.preventDefault()}>
                                              {value}
                                            </Link>
                                          ) : (
                                            value
                                          )}
                                        </Box>
                                      </Tooltip>
                                    )}
                                  </TableCell>
                                );
                              })}
                              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }}>
                                {rowTotals[assignee] || 0}
                              </TableCell>
                            </TableRow>
                          ))}
                          {(groupedData[category] || []).length === 0 && (
                            <TableRow>
                              <TableCell colSpan={months.length + 2} sx={{ pl: 4, color: 'text.secondary' }}>
                                No assignees in this group.
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}

            {/* Total row */}
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }}>Total</TableCell>
              {months.map(month => (
                <TableCell key={`total-${month}`} align="center" sx={{ fontWeight: 'bold', backgroundColor: '#e0e0e0' }}>
                  {columnTotals[month] || 0}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ fontWeight: 'bold', backgroundColor: '#bdbdbd' }}>
                {Object.values(columnTotals).reduce((sum, val) => sum + val, 0)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      
      <Dialog open={dialogOpen} onClose={handleDialogClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Tickets assigned to {extractNameFromEmail(selectedAssignee)} for {selectedMonth}
            </Typography>
            <IconButton onClick={handleDialogClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Total tickets: {ticketsData.length}
          </Typography>
          
          <TableContainer sx={{ maxHeight: '60vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Title</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Classification</TableCell>
                  <TableCell>Component</TableCell>
                  <TableCell>Environment</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created Date</TableCell>
                  <TableCell>Closed Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ticketsData.map(ticket => {
                  // Create the ADO URL directly here to ensure it's correct
                  const adoUrl = formatAdoUrl(ticket.id);
                  console.log(`Ticket ID: ${ticket.id}, ADO URL: ${adoUrl}`);
                  
                  return (
                    <TableRow key={ticket.id} hover>
                      <TableCell>
                        <a 
                          href={adoUrl}
                          target="_blank" 
                          rel="noopener noreferrer"
                        >
                          {ticket.id}
                        </a>
                      </TableCell>
                      <TableCell>{ticket.title || ''}</TableCell>
                      <TableCell>{ticket.priority || ''}</TableCell>
                      <TableCell>{ticket.investigationClassification || ''}</TableCell>
                      <TableCell>{ticket.componentPath || ''}</TableCell>
                      <TableCell>{ticket.environment || ''}</TableCell>
                      <TableCell>{ticket.state || ''}</TableCell>
                      <TableCell>
                        {ticket.createdDate ? new Date(ticket.createdDate).toLocaleDateString() : ''}
                      </TableCell>
                      <TableCell>
                        {ticket.closedDate ? new Date(ticket.closedDate).toLocaleDateString() : ''}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TsiAssigneePivotTable;
